<template>
  <SlideshowPanel icon="share" :title="title">
    <!-- The link first: one clear button, the address under it, then the services. What the
         card shares besides the link (the film) comes in the slot. -->
    <div class="slideshow-share-card__copy">
      <SlideshowCopyButton
        class="slideshow-share-card__copybtn"
        :copied="copied"
        @click="copyLink(linkInput)"
      />
      <input
        readonly
        ref="linkInput"
        :aria-label="texts.linkLabel"
        :value="pageUrl"
        @focus="($event.target as HTMLInputElement).select()"
      >
    </div>
    <p aria-live="polite" class="slideshow-share-card__note" v-if="note">
      {{ note }}
    </p>

    <div class="slideshow-share-card__via">
      <span class="slideshow-share-card__label">{{ texts.shareVia }}</span>
      <div class="slideshow-share-card__services">
        <button
          class="slideshow-share-card__service"
          type="button"
          v-for="service in SERVICES"
          :key="service"
          :aria-label="serviceLabel(service)"
          :title="serviceLabel(service)"
          @click="shareTo(service)"
        >
          <span aria-hidden="true" v-html="SERVICE_ICONS[service]" />
        </button>
      </div>
    </div>

    <slot />
  </SlideshowPanel>
</template>

<script lang="ts" setup>
  import { onMounted, ref } from "vue";
  import {
    type ShareLinkOptions,
    useShareLink,
  } from "../../composables/useShareLink";
  import {
    type Service,
    useShareServices,
  } from "../../composables/useShareServices";
  import { slideshowTexts } from "../../composables/useSlideshow";
  import { loadFacebookSdk } from "../../slideshow/share";
  import SlideshowCopyButton from "./SlideshowCopyButton.vue";
  import SlideshowPanel from "./SlideshowPanel.vue";

  /**
   * Sharing a link to the memorial page on a computer: copy it, or send it through a service.
   * The slideshow's share card and the memory book's share button both use it.
   */
  const props = defineProps<{
    title: string;
    /** The address shown and shared, before each channel's tags. */
    pageUrl: string;
    /** The deceased's name, for the message that goes with the link. */
    name: string;
    /** Each channel's address; the slideshow's tags by default. */
    tag?: ShareLinkOptions["tag"];
    /** Tracking instead of the slideshow's own. */
    reportCopied?: () => void;
    reportShared?: (service: Service) => void;
  }>();

  const texts = slideshowTexts();
  const linkInput = ref<HTMLInputElement | null>(null);
  const { copied, copyLink, note, textFor, urlFor } = useShareLink(
    () => props.pageUrl,
    () => props.name,
    "share_card",
    { onCopied: props.reportCopied, tag: props.tag }
  );
  const { SERVICES, SERVICE_ICONS, serviceLabel, shareTo } = useShareServices(
    urlFor,
    textFor,
    () => props.name,
    () => copyLink(linkInput.value),
    "share_card",
    props.reportShared
  );

  onMounted(() => loadFacebookSdk(document.documentElement.lang));
</script>
